import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type ComponentProps, type ReactNode, useId } from "react";

import { AnimatedGradientBorder } from "../animated-gradient-border/AnimatedGradientBorder";
import { AskMaraStarSvg, AskMaraSvg, ErrorNoInfoSvg } from "../assets/svg";
import { MaraLoader } from "../mara-loader/MaraLoader";
import { Markdown } from "../markdown/Markdown";
// import { PageStatus } from "../page-status/PageStatus";
import { StatusMessage } from "../status-message/StatusMessage";
import { blurFadeStyle } from "../utils";

export interface AskMaraAccordionProps {
  summary: string;
  value: string;
  id: string;
  details1: string;
  details2: string;
}

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    color: theme.palette.primary.main,
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: 0,
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
  "&.MuiAccordionSummary-root": { padding: "8px 16px 8px 16px" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    backgroundColor: theme.palette.common.white,
    borderRadius: "0 8px 8px 0",
    height: "36px",
    width: "36px",
    boxSizing: "border-box",
    alignItems: "center",
    transform: "none",
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
  // MUI rotates the whole wrapper on expand by default, which flips our asymmetric radius
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "none",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded .MuiSvgIcon-root": {
    transform: "rotate(180deg)",
  },
  // treat the button + expand icon as one control: hovering either highlights both
  "&:has(.ia-cta-button:hover) .MuiAccordionSummary-expandIconWrapper": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderRadius: "0 8px 8px 0",
  },
  "&:has(.MuiAccordionSummary-expandIconWrapper:hover) .ia-cta-button": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

// Visual-only CTA: AccordionSummary is already a <button>, so a real Button would nest buttons
const ExpandButtonStyled = styled("span")(({ theme }) => ({
  ...theme.typography.button,
  textTransform: "none",
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "center",
  height: "36px",
  padding: "6px 8px",
  boxSizing: "border-box",
  borderRadius: "8px 0 0 8px",
  color: `${theme.palette.primary.dark}`,
  backgroundColor: theme.palette.common.white,
  // override MUI's default (slower) button transition to match the expand icon's
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest,
  }),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const MetricChip = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  fontSize: "16px",
  color: theme.palette.primary.dark,
  backgroundColor: theme.palette.primary.L90,
  borderRadius: "8px",
}));

const AnimatedBorder = styled(AnimatedGradientBorder)({
  borderRadius: "8px",
  maxWidth: "816px",
});

const BadgeContainer = styled(Stack)(({ theme }) => ({
  width: "fit-content",
  backgroundColor: theme.palette.support[50],
  padding: "4px 8px",
  alignItems: "center",
  gap: "4px",
  borderRadius: "4px",
}));

const BadgeComponent = ({ data }: { data: InsightData }) => (
  <Stack sx={{ ...blurFadeStyle(2) }}>
    <BadgeContainer direction="row">
      <Typography variant="overline">{data.badgeLabel}</Typography>
      {data.icon ? data.icon : <AskMaraStarSvg width={14} height={14} />}
    </BadgeContainer>
  </Stack>
);

export interface InsightData {
  /** description, e.g. "Detecte un ahorro potencial en"  */
  title: string;
  /** Title or category of the detected saving (e.g., "Personal Operativo") */
  categoryTitle: string;
  /** Highlighted amount only, rendered with the subtitle variant (e.g., "389") */
  metricValue: string;
  /** Text before the highlighted amount (e.g., "$") */
  metricValueSymbol?: string;
  /** Text right after the highlighted amount (e.g., "M") */
  metricValueSuffix?: string;
  /** Text after the suffix (e.g., "por mes") */
  metricValueDescription?: string;
  /** Detailed description shown when expanded in markdown */
  descriptionInBlack?: string;
  /** Optional badge or tag label at the bottom (e.g., "Potential savings") */
  descriptionInBlue?: string;
  /** Optional badge or tag label at the bottom (e.g., "Potential savings") */
  badgeLabel?: string;
  /** Custom icon node for the header */
  icon?: ReactNode;
}

export interface InsightAnalysisProps extends ComponentProps<"div"> {
  data: InsightData;
  alwaysExpanded?: boolean;
  loading?: boolean;
  error?: Error | null;
}

export function InsightAnalysis({
  data,
  alwaysExpanded = false,
  loading = false,
  error,
  ...props
}: InsightAnalysisProps) {
  const id = useId();
  const expandableAccordion =
    !alwaysExpanded && Boolean(data.descriptionInBlack || loading || error);
  return (
    <AnimatedBorder>
      <div {...props}>
        <Accordion
          sx={(theme) => ({
            backgroundColor: theme.palette.support[50],
          })}
          expanded={alwaysExpanded ? true : undefined}
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <StyledAccordionSummary
            expandIcon={expandableAccordion ? <ExpandMoreIcon /> : null}
            aria-controls={`${id}-panel1-content`}
            id={`${id}-panel1-header`}
            tabIndex={alwaysExpanded ? -1 : undefined}
            sx={alwaysExpanded ? { pointerEvents: "none" } : undefined}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AskMaraSvg width={16} height={16} />
                <Markdown
                  variant="body1"
                  color="secondary.dark"
                >{`${data.title} ${data.categoryTitle}`}</Markdown>
              </Stack>

              <Stack direction="row" alignItems="stretch" gap={3}>
                <MetricChip>
                  <Typography variant="body1" color="inherit">
                    {data.metricValueSymbol}
                    <Typography
                      component="span"
                      variant="subtitle2"
                      color="inherit"
                    >
                      {data.metricValue}
                    </Typography>
                    {data.metricValueSuffix} {data.metricValueDescription}
                  </Typography>
                </MetricChip>
                {expandableAccordion && (
                  <ExpandButtonStyled
                    className="ia-cta-button"
                    aria-hidden="true"
                  >
                    Descubre cómo
                  </ExpandButtonStyled>
                )}
              </Stack>
            </Stack>
          </StyledAccordionSummary>
          <Stack
            display={expandableAccordion || alwaysExpanded ? "flex" : "none"}
            gap={2}
            margin="8px 16px"
            padding={2}
            sx={(theme) => ({
              backgroundColor: theme.palette.common.white,
              borderRadius: "8px",
            })}
            justifyContent="flex-start"
          >
            {(expandableAccordion || alwaysExpanded) &&
              (loading ? (
                <Stack gap={2}>
                  <Box sx={{ width: 50 }}>
                    <MaraLoader />
                  </Box>
                  {data.badgeLabel && <BadgeComponent data={data} />}
                </Stack>
              ) : error ? (
                <Stack
                  gap={2}
                  justifyContent="flex-start"
                  sx={blurFadeStyle(0)}
                >
                  <StatusMessage
                    title="¡Oh no! Algo salió mal"
                    description="Por favor, intenta recargando la página. "
                    icon={<ErrorNoInfoSvg />}
                  />
                  {data.badgeLabel && <BadgeComponent data={data} />}
                </Stack>
              ) : (
                <Stack gap={2}>
                  <AccordionDetails sx={[{ padding: 0 }, blurFadeStyle(0)]}>
                    <Markdown variant="body1">
                      {data.descriptionInBlack ?? ""}
                    </Markdown>
                  </AccordionDetails>
                  <AccordionDetails
                    sx={[
                      (theme) => ({
                        color: theme.palette.primary.dark,
                        padding: 0,
                      }),
                      blurFadeStyle(1),
                    ]}
                  >
                    <Markdown variant="body1" color="inherit">
                      {data.descriptionInBlue ?? ""}
                    </Markdown>
                  </AccordionDetails>
                  {data.badgeLabel && <BadgeComponent data={data} />}
                </Stack>
              ))}
          </Stack>
        </Accordion>
      </div>
    </AnimatedBorder>
  );
}
